import ungapStructuredClone from '@ungap/structured-clone';

if (typeof globalThis.structuredClone !== 'function') {
  globalThis.structuredClone = ungapStructuredClone as typeof structuredClone;
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { getCookieConsentValue } from 'react-cookie-consent';
import { persistor, store } from './store';
import './services/localization/Localization';
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/700.css';
import './styles/index.css';
import 'react-multi-carousel/lib/styles.css';
import 'react-mobile-style-picker/dist/index.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import '@adyen/adyen-web/styles/adyen.css';
import * as Sentry from '@sentry/react';
import MobileAppProvider from './providers/MobileAppProvider';
import beforeSendError from './services/helpers/SentryFilters';
import { ENVIRONMENTS } from './services/exports/Constants';
import initTapDebug from './services/debug/tapDebug';
import { actionCreators } from './store/actions';
import { enableSentryReplay } from './services/helpers/SentryReplay';
import { installDomMutationGuard } from './services/helpers/DomMutationGuard';
import { handleVitePreloadError } from './services/helpers/StaleChunkReload';
import { reportStuckStoreReload } from './store/middleware/DispatchWatchdog';
import { cleanLocationPath } from './utils/clean-location';
import { captureFranchiseOverride } from './services/helpers/FranchiseOverride';

declare global {
  interface Window {
    Cypress: any;
    store: any;
    gtag: any;
  }
}

/**
 * Anchored origin patterns for the Foodamigos APIs, so `sentry-trace`/`baggage` are only
 * attached to our own backends and never to third-party hosts.
 */
function apiOriginPatterns(baseUrls: Array<string | undefined>): RegExp[] {
  return baseUrls.flatMap((baseUrl) => {
    try {
      const { origin } = new URL(baseUrl ?? '');

      return [new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(/|$)`)];
    } catch {
      return [];
    }
  });
}

/**
 * Trace sample rate from `VITE_SENTRY_TRACES_SAMPLE_RATE`. An empty, malformed or out-of-range value
 * falls back to the default rather than silently disabling tracing (`Number('')` is 0).
 */
function tracesSampleRate(value: string | undefined): number {
  const rate = parseFloat(value ?? '');

  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : 0.02;
}

installDomMutationGuard();
cleanLocationPath();
captureFranchiseOverride();

if ([ENVIRONMENTS.PROD, ENVIRONMENTS.STAGING].includes(import.meta.env.VITE_MODE)) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate: tracesSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    tracePropagationTargets: apiOriginPatterns([
      import.meta.env.VITE_API_BASE_URL,
      import.meta.env.VITE_STOREFRONT_API_BASE_URL,
    ]),
    // Replay only the session that led to an error (buffered, text and media masked); no continuous
    // session recording — Mixpanel covers that, and every replay above the 50 included is billed.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 10,
    environment: import.meta.env.VITE_MODE?.toLowerCase(),
    attachStacktrace: true,
    enableLogs: true,
    integrations: [
      Sentry.consoleLoggingIntegration({ levels: ['info', 'warn', 'error'] }),
      Sentry.browserTracingIntegration({
        // Resource timing (one span per image, script, stylesheet or font on a menu page), mark/measure and
        // long-animation-frame spans were ~75% of this project's span volume and are not what a trace is
        // opened for; page loads, navigations, web vitals and the API calls stay.
        ignoreResourceSpans: [
          'resource.script',
          'resource.css',
          'resource.img',
          'resource.link',
          'resource.font',
          'resource.video',
          'resource.iframe',
          'resource.other',
          'resource.fetch',
          'resource.xmlhttprequest',
          'resource.beacon',
        ],
        ignorePerformanceApiSpans: [/.*/],
        enableLongAnimationFrame: false,
        enableLongTask: false,
      }),
      // Session replay is added only after cookie consent (see SentryReplay).
    ],
    beforeSendLog: function (log) {
      const message = typeof log.message === 'string' ? log.message : '';

      if (
        /^Amplitude Logger|^Facebook Pixel|api2\.amplitude\.com|google\.maps\.places\.Autocomplete is not available|google\.maps\.Marker is deprecated|Cannot get CSS styles from text's parentNode|paymentMethodsResponse was provided but no payment methods were found/.test(
          message,
        )
      ) {
        return null;
      }

      return log;
    },
    beforeSend: beforeSendError,
  });

  // The cookie is read directly: Redux has not rehydrated yet. AnalyticsProvider keeps it in sync after.
  if (getCookieConsentValue() === 'true') {
    enableSentryReplay();
  }
}

reportStuckStoreReload();

window.addEventListener('vite:preloadError', handleVitePreloadError);

// Armed only by `?__tapdebug=1`; a no-op for everyone else. Started BEFORE React so it is
// still recording if React never becomes interactive.
initTapDebug();

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      {/* The CookieConsent cookie is the source of truth for consent; the persisted Redux copy can
          outlive it (cleared or expired cookie), which would grant tracking nobody agreed to. */}
      <PersistGate
        loading={null}
        persistor={persistor}
        onBeforeLift={() => store.dispatch(actionCreators.appStatus.setCookieConsent(getCookieConsentValue()))}
      >
        <MobileAppProvider>
          <App />
        </MobileAppProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);

// Exposes the Redux store to the e2e suites, which drive app state through it rather than
// through a dozen extra selectors. `window.Cypress` covers the Cypress runner; the env flag
// covers Playwright, which never sets that global. The flag is set in
// playwright.config.ts -> webServer.env, deliberately NOT in .env.development (that file is
// committed and feeds the app-dev deployment).
//
// Vite statically replaces `import.meta.env.*`, so a production build compiles the second arm
// to `'undefined' === 'true'` and the minifier removes it entirely — verified: the build emits
// `window.Cypress&&(window.store=e)`. Note the ASSIGNMENT still ships, via the `window.Cypress`
// arm that is already there today; collapsing to the env check alone at cutover (when the
// Cypress suite goes away) is what finally compiles this out of production altogether.
if (window.Cypress || import.meta.env.VITE_EXPOSE_REDUX_STORE === 'true') {
  window.store = store;
}

reportWebVitals();
